import type {
  CatalogueField,
  Narrowing,
  QuantityBreak,
} from "@shared-core/data/catalogue-types";
import { Card } from "../../primitives/card";
import { Star } from "lucide-react";

// The dictionary rewire's narrowing editor (design doc "Narrowing: the
// constraint vocabulary"): restrict a field to a subset of the parent's
// resolved options, pin a field to exactly one, restrict the quantity
// ladder to a subset of the parent's resolved quantities. Narrowing may
// only remove, never add — there is no add-option/add-field affordance
// here, unlike CatalogueFieldEditor (which authors dictionary spec, a
// different verb). Operates on the *parent's resolved* fields/breaks —
// the space this derivation narrows — and reports the resulting Narrowing
// back to the host.

function fieldNarrowingFor(narrowing: Narrowing, key: string) {
  return narrowing.fields?.[key] ?? { allowedOptions: [], pinned: null };
}

function isOptionAllowed(
  narrowing: Narrowing,
  fieldKey: string,
  code: string,
): boolean {
  const allowed = fieldNarrowingFor(narrowing, fieldKey).allowedOptions;
  return !allowed || allowed.length === 0 || allowed.includes(code);
}

export function NarrowingEditor({
  parentFields,
  parentQuantityBreaks,
  narrowing,
  onChange,
}: {
  /** The parent's resolved fields — the space this derivation may narrow. */
  parentFields: CatalogueField[];
  /** The parent's resolved quantity ladder. */
  parentQuantityBreaks: QuantityBreak[];
  narrowing: Narrowing;
  onChange: (next: Narrowing) => void;
}) {
  const narrowableFields = parentFields.filter(
    (f) => f.mode !== "fixed" && (f.options?.length ?? 0) > 1,
  );

  const toggleOption = (fieldKey: string, code: string) => {
    const field = parentFields.find((f) => f.key === fieldKey);
    const allCodes = (field?.options ?? []).map((o) => o.code);
    const current = fieldNarrowingFor(narrowing, fieldKey);
    // Empty allowedOptions means "everything allowed" — the first toggle-off
    // materialises the full set minus the one just removed, rather than
    // starting from an empty (nothing-allowed) list.
    const baseline =
      !current.allowedOptions || current.allowedOptions.length === 0
        ? allCodes
        : current.allowedOptions;
    const nextAllowed = baseline.includes(code)
      ? baseline.filter((c) => c !== code)
      : [...baseline, code];
    // A pin that falls outside the new allowed set is no longer valid — drop it
    // rather than submit a contradiction the backend would reject.
    const nextPinned =
      current.pinned && !nextAllowed.includes(current.pinned)
        ? null
        : (current.pinned ?? null);
    // Restricting to the full set is the same as no restriction — omit it so
    // the narrowing stays a minimal, honest diff.
    const isFullSet =
      nextAllowed.length === allCodes.length &&
      allCodes.every((c) => nextAllowed.includes(c));
    setFieldNarrowing(fieldKey, {
      allowedOptions: isFullSet ? [] : nextAllowed,
      pinned: nextPinned,
    });
  };

  const setPin = (fieldKey: string, code: string | null) => {
    const current = fieldNarrowingFor(narrowing, fieldKey);
    setFieldNarrowing(fieldKey, {
      allowedOptions: current.allowedOptions ?? [],
      pinned: code,
    });
  };

  const setFieldNarrowing = (
    fieldKey: string,
    fn: { allowedOptions: string[]; pinned: string | null },
  ) => {
    const fields = { ...(narrowing.fields ?? {}) };
    if (fn.allowedOptions.length === 0 && fn.pinned == null) {
      delete fields[fieldKey];
    } else {
      fields[fieldKey] = fn;
    }
    onChange({ ...narrowing, fields });
  };

  const toggleQuantity = (qty: number) => {
    const allQuantities = parentQuantityBreaks.map((b) => b.qty);
    const baseline =
      !narrowing.quantities || narrowing.quantities.length === 0
        ? allQuantities
        : narrowing.quantities;
    const next = baseline.includes(qty)
      ? baseline.filter((q) => q !== qty)
      : [...baseline, qty];
    const isFullSet =
      next.length === allQuantities.length &&
      allQuantities.every((q) => next.includes(q));
    onChange({ ...narrowing, quantities: isFullSet ? [] : next });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Narrowing restricts what this item offers from its parent&apos;s spec —
        it can remove options and quantities, or pin a field to one value, but
        never add anything the parent doesn&apos;t already have.
      </p>

      {narrowableFields.length === 0 ? (
        <Card className="p-4 text-sm text-muted-foreground">
          Nothing to narrow — the parent has no multi-option fields.
        </Card>
      ) : (
        narrowableFields.map((field) => (
          <Card key={field.key} className="p-4 space-y-2">
            <div className="text-sm font-medium">{field.label}</div>
            <div className="flex flex-wrap items-center gap-2">
              {(field.options ?? []).map((opt) => {
                const allowed = isOptionAllowed(narrowing, field.key, opt.code);
                const pinned =
                  fieldNarrowingFor(narrowing, field.key).pinned === opt.code;
                return (
                  <span
                    key={opt.code}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm ${
                      !allowed
                        ? "border-border text-muted-foreground line-through opacity-50"
                        : pinned
                          ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5"
                          : "border-border"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleOption(field.key, opt.code)}
                      title={allowed ? "Restrict away" : "Allow"}
                    >
                      {opt.label}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setPin(field.key, pinned ? null : opt.code)
                      }
                      disabled={!allowed}
                      title={
                        pinned ? "Pinned — click to unpin" : "Pin this value"
                      }
                      className={
                        pinned
                          ? "text-[color:var(--color-primary)]"
                          : "text-muted-foreground hover:text-[color:var(--color-primary)] disabled:opacity-40"
                      }
                    >
                      <Star
                        className={`size-3.5 ${pinned ? "fill-current" : ""}`}
                      />
                    </button>
                  </span>
                );
              })}
            </div>
          </Card>
        ))
      )}

      {parentQuantityBreaks.length > 0 && (
        <Card className="p-4 space-y-2">
          <div className="text-sm font-medium">Quantities offered</div>
          <div className="flex flex-wrap items-center gap-2">
            {parentQuantityBreaks.map((b) => {
              const allowed =
                !narrowing.quantities ||
                narrowing.quantities.length === 0 ||
                narrowing.quantities.includes(b.qty);
              return (
                <button
                  key={b.qty}
                  type="button"
                  onClick={() => toggleQuantity(b.qty)}
                  className={`rounded-md border px-2.5 py-1 text-sm tabular-nums ${
                    allowed
                      ? "border-border"
                      : "border-border text-muted-foreground line-through opacity-50"
                  }`}
                  title={allowed ? "Restrict away" : "Allow"}
                >
                  {b.qty.toLocaleString()}
                </button>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
